if (!customElements.get('byte-avis-cart-summary')) {
  class ByteAvisCartSummary extends HTMLElement {
    static #BYPASS_FLAG = '__byteAvisCartSummaryBypass';

    constructor() {
      super();

      this.isOpen = false;
      this.closeResolve = null;
      this.boundHandleSubmitCapture = this.handleSubmitCapture.bind(this);
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      document.addEventListener('submit', this.boundHandleSubmitCapture, true);
      this.addEventListener('click', this.boundHandleClick);
      document.addEventListener('keydown', this.boundHandleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.boundHandleSubmitCapture, true);
      this.removeEventListener('click', this.boundHandleClick);
      document.removeEventListener('keydown', this.boundHandleKeydown);
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-avis-summary-action]');
      if (!actionTrigger || !this.isOpen) return;

      const action = actionTrigger.dataset.avisSummaryAction;
      if (action !== 'confirm' && action !== 'close') return;

      this.#closeSummary(action === 'confirm');
    }

    handleKeydown(event) {
      if (!this.isOpen || event.key !== 'Escape') return;

      event.preventDefault();
      this.#closeSummary(false);
    }

    async handleSubmitCapture(event) {
      if (window[ByteAvisCartSummary.#BYPASS_FLAG]) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.type !== 'add-to-cart-form') return;

      const selections = this.#collectSelections();
      if (selections.length === 0) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const confirmed = await this.#showSummary(selections, form);
      if (!confirmed) return;

      window[ByteAvisCartSummary.#BYPASS_FLAG] = true;
      form.requestSubmit();
      window[ByteAvisCartSummary.#BYPASS_FLAG] = false;
    }

    #collectSelections() {
      const avisRoot = document.getElementById('avpoptions-container__v2');
      if (!avisRoot) return [];

      const selections = new Map();

      avisRoot
        .querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')
        .forEach((input) => {
          const fieldName = this.#getFieldName(input);
          const displayValue = this.#getDisplayValue(input);

          if (!fieldName || !displayValue) return;

          selections.set(fieldName, displayValue);
        });

      avisRoot.querySelectorAll('select').forEach((select) => {
        const fieldName = this.#getFieldName(select);
        const displayValue = this.#getSelectDisplayValue(select);

        if (!fieldName || !displayValue) return;

        selections.set(fieldName, displayValue);
      });

      avisRoot
        .querySelectorAll('input[type="text"], input[type="number"], input[type="tel"], textarea')
        .forEach((input) => {
          if (input.disabled || input.type === 'hidden' || input.closest('[hidden]')) return;

          const fieldName = this.#getFieldName(input);
          const displayValue = (input.value || '').trim();

          if (!fieldName || !displayValue) return;

          selections.set(fieldName, displayValue);
        });

      return Array.from(selections.entries())
        .filter(([label]) => !ByteAvisCartSummary.#EXCLUDED_FIELD_LABELS.has(label))
        .map(([label, value]) => ({
          label,
          value,
        }));
    }

    static #EXCLUDED_FIELD_LABELS = new Set(['Select Options']);

    static #FIELD_LABEL_MAP = {
      'Görüntü örneği 5': 'Corner Style',
    };

    #getFieldName(element) {
      const rawLabel = (
        element.getAttribute('field-name') ||
        element.getAttribute('data-field-name') ||
        element.getAttribute('aria-label') ||
        ''
      ).trim();

      return ByteAvisCartSummary.#FIELD_LABEL_MAP[rawLabel] || rawLabel;
    }

    #getDisplayValue(input) {
      const descriptionValue = (input.getAttribute('des_value') || '').trim();
      if (descriptionValue) return descriptionValue;

      const swatch = input.closest(
        '.avp-productoptionswatchwrapper, .ap-option-value-swatch, .avp-swatch-item'
      );

      if (swatch) {
        const swatchLabel = swatch.querySelector(
          '.avp-swatch-label, .ap-option-value-label, .avp-option-value-label'
        );

        if (swatchLabel?.textContent?.trim()) {
          return swatchLabel.textContent.trim();
        }
      }

      const associatedLabel = input.id
        ? document.querySelector(`#avpoptions-container__v2 label[for="${CSS.escape(input.id)}"]`)
        : null;

      if (associatedLabel?.textContent?.trim()) {
        return associatedLabel.textContent.trim();
      }

      return (input.value || '').trim();
    }

    #getSelectDisplayValue(select) {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return '';

      return (selectedOption.textContent || selectedOption.value || '').trim();
    }

    #showSummary(selections, form) {
      if (!this.listEl || !this.overlayEl) return Promise.resolve(true);

      this.#renderSelections(selections, form);

      this.overlayEl.hidden = false;
      this.isOpen = true;
      this.classList.add('is-active');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.confirmButton?.focus();

      return new Promise((resolve) => {
        this.closeResolve = resolve;
      });
    }

    #renderSelections(selections, form) {
      this.listEl.replaceChildren();

      for (const selection of selections) {
        this.listEl.append(this.#createSummaryItem(selection.label, selection.value));
      }

      const quantity = this.#getQuantity(form);
      if (quantity !== null) {
        this.listEl.append(this.#createSummaryItem(this.quantityLabel, String(quantity)));
      }

      const price = this.#getDisplayPrice();
      if (price) {
        this.listEl.append(this.#createSummaryItem(this.priceLabel, price));
      }
    }

    #createSummaryItem(label, value) {
      const item = document.createElement('div');
      item.className = 'avis-cart-summary__item';
      item.setAttribute('role', 'listitem');

      const labelEl = document.createElement('span');
      labelEl.className = 'avis-cart-summary__label';
      labelEl.textContent = label;

      const valueEl = document.createElement('span');
      valueEl.className = 'avis-cart-summary__value';
      valueEl.textContent = value;

      item.append(labelEl, valueEl);
      return item;
    }

    #getQuantity(form) {
      const quantityInput = this.#findQuantityInput(form);
      const quantity = parseInt(quantityInput?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : null;
    }

    #findQuantityInput(form) {
      if (this.quantityInputSelector) {
        const quantityInput = document.querySelector(this.quantityInputSelector);
        if (quantityInput instanceof HTMLInputElement) return quantityInput;
      }

      if (form?.id) {
        const associatedInput = document.querySelector(
          `input[name="quantity"][form="${CSS.escape(form.id)}"]`
        );
        if (associatedInput instanceof HTMLInputElement) return associatedInput;
      }

      const fallbackInput =
        form?.querySelector('[name="quantity"]') ||
        document.querySelector('.quantity__input[name="quantity"]') ||
        document.querySelector('input[name="quantity"]');

      return fallbackInput instanceof HTMLInputElement ? fallbackInput : null;
    }

    #getDisplayPrice() {
      if (!this.priceSource) return '';

      // Avis clones the theme price into `.price__container.apo-variant-price` and hides the
      // original, so prefer the visible clone when it exists.
      const priceContainer =
        this.priceSource.querySelector('.price__container.apo-variant-price') ||
        this.priceSource.querySelector('.price__container');
      if (!priceContainer) return '';

      // Avis writes the option-adjusted total into `.price__sale .price-item--sale` even when the
      // product is not on sale (that block stays hidden), so it is the source of truth.
      const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');
      const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
      return salePrice?.textContent.trim() || regularPrice?.textContent.trim() || '';
    }

    #closeSummary(confirmed = false) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      window.setTimeout(() => {
        if (!this.isOpen && this.overlayEl) {
          this.overlayEl.hidden = true;
        }
      }, 200);

      if (this.closeResolve) {
        this.closeResolve(confirmed);
        this.closeResolve = null;
      }
    }

    #cacheElements() {
      this.overlayEl = this.querySelector('[data-avis-summary-overlay]');
      this.listEl = this.querySelector('[data-avis-summary-list]');
      this.confirmButton = this.querySelector('[data-avis-summary-action="confirm"]');
      this.priceSourceSelector = this.dataset.priceSource;
      this.priceSource = this.priceSourceSelector
        ? document.querySelector(this.priceSourceSelector)
        : null;
      this.quantityInputSelector = this.dataset.quantityInput;
      this.quantityLabel = this.dataset.quantityLabel || 'Quantity';
      this.priceLabel = this.dataset.priceLabel || 'Price';
    }
  }

  customElements.define('byte-avis-cart-summary', ByteAvisCartSummary);
}
